import { useEffect, useState } from "react";
import type { EtapaAutomatizacion, EtapaChecklistItem, EtapaDocumentoSugerido } from "../../../shared/types";
import { Boton } from "../../components/Boton";
import { Cargando } from "../../components/Cargando";
import { Input, Select } from "../../components/Formulario";
import { api, ErrorApi } from "../../lib/apiClient";

// Extraído de ProcesosTrabajoPage.tsx (Vista Tabla clásica) para que el
// Diseñador Visual (DisenadorVisualProceso.tsx / EtapaTarjetaVisual.tsx)
// pueda reutilizar exactamente los mismos 3 editores sin duplicar lógica
// ni crear una dependencia circular entre módulos. Sin cambios de
// comportamiento respecto al original.
//
// Checklist/documentos sugeridos/automatizaciones se editan como listas
// completas (agregar/quitar filas localmente, un único botón "Guardar" que
// reemplaza todo del lado del servidor) — mismo criterio que ya usa PUT
// /distribucion-gastos con los porcentajes por unidad: más simple de
// entender para el abogado que un CRUD ítem por ítem.

export function EditorChecklist({
  etapaId,
  puedeAdministrar,
  onError,
  onExito,
}: {
  etapaId: string;
  puedeAdministrar: boolean;
  onError: (m: string) => void;
  onExito: () => void;
}) {
  const [items, setItems] = useState<{ texto: string; obligatorio: boolean }[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setCargando(true);
    api
      .get<{ items: EtapaChecklistItem[] }>(`/procesos/etapas/${etapaId}/checklist`)
      .then((d) => setItems(d.items.map((i) => ({ texto: i.texto, obligatorio: i.obligatorio }))))
      .catch(() => onError("No se pudo cargar el checklist."))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaId]);

  async function guardar() {
    setGuardando(true);
    try {
      await api.put(`/procesos/etapas/${etapaId}/checklist`, { items: items.filter((i) => i.texto.trim()) });
      onExito();
    } catch (err) {
      onError(err instanceof ErrorApi ? err.message : "No se pudo guardar el checklist.");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <Cargando />;

  return (
    <div className="space-y-2">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">Checklist</h5>
      <p className="text-xs text-texto-secundario">
        Un ítem marcado como "Obligatorio" impide avanzar el expediente a la siguiente etapa hasta completarse.
      </p>
      {items.map((item, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={item.texto}
            onChange={(e) =>
              setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, texto: e.target.value } : it)))
            }
            placeholder="Ítem del checklist"
            disabled={!puedeAdministrar}
          />
          <label className="flex shrink-0 items-center gap-1 text-xs text-texto-secundario">
            <input
              type="checkbox"
              checked={item.obligatorio}
              disabled={!puedeAdministrar}
              onChange={(e) =>
                setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, obligatorio: e.target.checked } : it)))
              }
            />
            Obligatorio
          </label>
          {puedeAdministrar && (
            <Boton
              type="button"
              variante="fantasma"
              tamano="sm"
              onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
            >
              ✕
            </Boton>
          )}
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-texto-secundario">Sin ítems de checklist.</p>}
      {puedeAdministrar && (
        <div className="flex justify-between pt-1">
          <Boton
            type="button"
            variante="secundario"
            tamano="sm"
            onClick={() => setItems((prev) => [...prev, { texto: "", obligatorio: true }])}
          >
            + Agregar ítem
          </Boton>
          <Boton type="button" tamano="sm" cargando={guardando} onClick={guardar}>
            Guardar
          </Boton>
        </div>
      )}
    </div>
  );
}

export function EditorDocumentosSugeridos({
  etapaId,
  puedeAdministrar,
  onError,
  onExito,
}: {
  etapaId: string;
  puedeAdministrar: boolean;
  onError: (m: string) => void;
  onExito: () => void;
}) {
  const [items, setItems] = useState<string[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setCargando(true);
    api
      .get<{ items: EtapaDocumentoSugerido[] }>(`/procesos/etapas/${etapaId}/documentos-sugeridos`)
      .then((d) => setItems(d.items.map((i) => i.nombre)))
      .catch(() => onError("No se pudieron cargar los documentos sugeridos."))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaId]);

  async function guardar() {
    setGuardando(true);
    try {
      await api.put(`/procesos/etapas/${etapaId}/documentos-sugeridos`, {
        items: items.filter((n) => n.trim()).map((nombre) => ({ nombre })),
      });
      onExito();
    } catch (err) {
      onError(err instanceof ErrorApi ? err.message : "No se pudieron guardar los documentos sugeridos.");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <Cargando />;

  return (
    <div className="space-y-2">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">Documentos sugeridos</h5>
      <p className="text-xs text-texto-secundario">Solo una lista orientativa — no bloquea ni genera archivos.</p>
      {items.map((nombre, i) => (
        <div key={i} className="flex items-center gap-2">
          <Input
            value={nombre}
            onChange={(e) => setItems((prev) => prev.map((n, idx) => (idx === i ? e.target.value : n)))}
            placeholder="Nombre del documento"
            disabled={!puedeAdministrar}
          />
          {puedeAdministrar && (
            <Boton
              type="button"
              variante="fantasma"
              tamano="sm"
              onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
            >
              ✕
            </Boton>
          )}
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-texto-secundario">Sin documentos sugeridos.</p>}
      {puedeAdministrar && (
        <div className="flex justify-between pt-1">
          <Boton type="button" variante="secundario" tamano="sm" onClick={() => setItems((prev) => [...prev, ""])}>
            + Agregar documento
          </Boton>
          <Boton type="button" tamano="sm" cargando={guardando} onClick={guardar}>
            Guardar
          </Boton>
        </div>
      )}
    </div>
  );
}

interface FilaAutomatizacion {
  tipo_actuacion: string;
  descripcion: string;
  genera_vencimiento: boolean;
  tipo_evento_vencimiento: string;
  dias_vencimiento: string;
  prioridad_vencimiento: string;
}

const FILA_AUTOMATIZACION_VACIA: FilaAutomatizacion = {
  tipo_actuacion: "",
  descripcion: "",
  genera_vencimiento: false,
  tipo_evento_vencimiento: "",
  dias_vencimiento: "",
  prioridad_vencimiento: "Media",
};

export function EditorAutomatizaciones({
  etapaId,
  puedeAdministrar,
  onError,
  onExito,
}: {
  etapaId: string;
  puedeAdministrar: boolean;
  onError: (m: string) => void;
  onExito: () => void;
}) {
  const [items, setItems] = useState<FilaAutomatizacion[]>([]);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  useEffect(() => {
    setCargando(true);
    api
      .get<{ items: EtapaAutomatizacion[] }>(`/procesos/etapas/${etapaId}/automatizaciones`)
      .then((d) =>
        setItems(
          d.items.map((i) => ({
            tipo_actuacion: i.tipo_actuacion,
            descripcion: i.descripcion,
            genera_vencimiento: i.genera_vencimiento,
            tipo_evento_vencimiento: i.tipo_evento_vencimiento ?? "",
            dias_vencimiento: i.dias_vencimiento != null ? String(i.dias_vencimiento) : "",
            prioridad_vencimiento: i.prioridad_vencimiento ?? "Media",
          })),
        ),
      )
      .catch(() => onError("No se pudieron cargar las automatizaciones."))
      .finally(() => setCargando(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [etapaId]);

  async function guardar() {
    const validas = items.filter((i) => i.tipo_actuacion.trim() && i.descripcion.trim());
    if (validas.some((i) => i.genera_vencimiento && (!i.tipo_evento_vencimiento.trim() || !i.dias_vencimiento))) {
      onError("Una automatización con vencimiento requiere tipo de evento y días de vencimiento.");
      return;
    }
    setGuardando(true);
    try {
      await api.put(`/procesos/etapas/${etapaId}/automatizaciones`, {
        items: validas.map((i) => ({
          tipo_actuacion: i.tipo_actuacion,
          descripcion: i.descripcion,
          genera_vencimiento: i.genera_vencimiento,
          tipo_evento_vencimiento: i.genera_vencimiento ? i.tipo_evento_vencimiento : null,
          dias_vencimiento: i.genera_vencimiento ? Number(i.dias_vencimiento) : null,
          prioridad_vencimiento: i.genera_vencimiento ? i.prioridad_vencimiento : null,
        })),
      });
      onExito();
    } catch (err) {
      onError(err instanceof ErrorApi ? err.message : "No se pudieron guardar las automatizaciones.");
    } finally {
      setGuardando(false);
    }
  }

  if (cargando) return <Cargando />;

  return (
    <div className="space-y-3">
      <h5 className="text-xs font-semibold uppercase tracking-wide text-texto-secundario">
        Automatizaciones al entrar a esta etapa
      </h5>
      <p className="text-xs text-texto-secundario">
        Crea una Actuación automáticamente (y, si corresponde, su vencimiento de Agenda) al mover un expediente a
        esta etapa.
      </p>
      {items.map((item, i) => (
        <div key={i} className="space-y-1.5 rounded-lg border border-borde p-2.5">
          <Input
            value={item.tipo_actuacion}
            onChange={(e) =>
              setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, tipo_actuacion: e.target.value } : it)))
            }
            placeholder="Tipo de actuación (ej. Diligencia)"
            disabled={!puedeAdministrar}
          />
          <Input
            value={item.descripcion}
            onChange={(e) =>
              setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, descripcion: e.target.value } : it)))
            }
            placeholder="Descripción"
            disabled={!puedeAdministrar}
          />
          <label className="flex items-center gap-1.5 text-xs text-texto-secundario">
            <input
              type="checkbox"
              checked={item.genera_vencimiento}
              disabled={!puedeAdministrar}
              onChange={(e) =>
                setItems((prev) =>
                  prev.map((it, idx) => (idx === i ? { ...it, genera_vencimiento: e.target.checked } : it)),
                )
              }
            />
            Generar vencimiento de Agenda
          </label>
          {item.genera_vencimiento && (
            <div className="grid grid-cols-3 gap-2">
              <Input
                value={item.tipo_evento_vencimiento}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, idx) => (idx === i ? { ...it, tipo_evento_vencimiento: e.target.value } : it)),
                  )
                }
                placeholder="Tipo de evento"
                disabled={!puedeAdministrar}
              />
              <Input
                type="number"
                min={0}
                value={item.dias_vencimiento}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, idx) => (idx === i ? { ...it, dias_vencimiento: e.target.value } : it)),
                  )
                }
                placeholder="Días"
                disabled={!puedeAdministrar}
              />
              <Select
                value={item.prioridad_vencimiento}
                onChange={(e) =>
                  setItems((prev) =>
                    prev.map((it, idx) => (idx === i ? { ...it, prioridad_vencimiento: e.target.value } : it)),
                  )
                }
                disabled={!puedeAdministrar}
              >
                <option value="Alta">Alta</option>
                <option value="Media">Media</option>
                <option value="Baja">Baja</option>
              </Select>
            </div>
          )}
          {puedeAdministrar && (
            <div className="flex justify-end">
              <Boton
                type="button"
                variante="fantasma"
                tamano="sm"
                onClick={() => setItems((prev) => prev.filter((_, idx) => idx !== i))}
              >
                Quitar
              </Boton>
            </div>
          )}
        </div>
      ))}
      {items.length === 0 && <p className="text-xs text-texto-secundario">Sin automatizaciones configuradas.</p>}
      {puedeAdministrar && (
        <div className="flex justify-between pt-1">
          <Boton
            type="button"
            variante="secundario"
            tamano="sm"
            onClick={() => setItems((prev) => [...prev, { ...FILA_AUTOMATIZACION_VACIA }])}
          >
            + Agregar automatización
          </Boton>
          <Boton type="button" tamano="sm" cargando={guardando} onClick={guardar}>
            Guardar
          </Boton>
        </div>
      )}
    </div>
  );
}
